// Server-render client. Documents (multi-page designs and/or designs with a
// data table) can't be produced by the client-side snapshot export: the data
// band paginates server-side (report.ts). This posts the .olm v2 design + the
// imported dataset to `${VITE_IMPORT_API}/render/report` and returns the PDF.
// Shares the build-time endpoint/token config with the AI import client.
import { saveOlmObject } from './olmfile'
import { getPages } from './pages'
import { labelMm } from './canvas'
import { state } from './state'
import { lqError, rowScope } from './liquid'

const API = ((import.meta.env as any).VITE_IMPORT_API as string | undefined)?.replace(/\/$/, '') ?? ''
const TOKEN = ((import.meta.env as any).VITE_IMPORT_TOKEN as string | undefined) ?? ''

export function isRenderConfigured(): boolean {
  return !!API
}

/** A "document" = a document-category page size (A4/Letter…), multiple pages,
 *  or any page carrying a data table — i.e. a design whose true output (vector
 *  text, Liquid evaluation, pagination) only exists server-side. A static Table
 *  on a LABEL stays client-side (labels print from the canvas snapshot). */
export function isDocumentDesign(): boolean {
  if (state.currentSize.category === 'document') return true
  const pages = getPages()
  if (pages.length > 1) return true
  return pages.some((p) => (p.json?.objects ?? []).some((o: any) =>
    ['datatable', 'dynamictable'].includes(String(o?.type ?? '').toLowerCase())))
}

export interface TemplateIssue { where: string; error: string }

/** Validate every Liquid template in the document against the imported dataset
 *  (strict: unknown filters AND unknown fields are errors). A bad template would
 *  otherwise ship as raw code / a blank in the rendered PDF. Scopes mirror the
 *  server: free text + static-table cells + summaries = ROOT ({rows}); a Data
 *  Table's template row = the first data record. Skipped when nothing is
 *  imported (field names are unknowable). */
export function collectTemplateIssues(): TemplateIssue[] {
  const rows = state.batchData ?? []
  if (!rows.length) return []
  const root = { rows }
  const firstRow = rowScope(rows[0] ?? {})
  const issues: TemplateIssue[] = []
  const check = (tpl: unknown, scope: Record<string, any>, where: string): void => {
    const err = lqError(tpl, scope)
    if (err) issues.push({ where, error: err.split('\n')[0] })
  }

  getPages().forEach((p, pi) => {
    const loc = `page ${pi + 1}`
    const walk = (objs: any[]): void => {
      for (const o of objs ?? []) {
        const t = String(o?.type ?? '').toLowerCase()
        if (['text', 'i-text', 'textbox'].includes(t)) {
          check(o.text, root, `${loc}, text "${String(o.text ?? '').slice(0, 30)}"`)
        } else if (t === 'dynamictable') {
          for (const cd of o.cellData ?? []) {
            if (cd.row === 0) continue // header row is literal
            check(cd.text, cd.row === 1 ? firstRow : root,
              `${loc}, data table ${cd.row === 1 ? 'template' : 'summary'} cell (row ${cd.row + 1}, col ${cd.col + 1})`)
          }
        } else if (t === 'table') {
          for (const cd of o.cellData ?? []) {
            check(cd.text, root, `${loc}, table cell (row ${cd.row + 1}, col ${cd.col + 1})`)
          }
        } else if (t === 'datatable') {
          for (const col of o.columns ?? []) check(col.cell, firstRow, `${loc}, data table column "${col.header}"`)
          ;(o.staticRows ?? []).forEach((r: any[], ri: number) => r.forEach((cell) =>
            check(typeof cell === 'string' ? cell : cell?.content, root, `${loc}, summary row ${ri + 1}`)))
        } else if (Array.isArray(o?.objects)) {
          walk(o.objects)
        }
      }
    }
    walk(p.json?.objects ?? [])
  })
  return issues
}

/** jsPDF page format for the current size: named when it's a standard page,
 *  else explicit [w,h] in pt. */
function pageFormat(): { format: 'a4' | 'letter' | number[]; orientation: 'portrait' | 'landscape' } {
  const { w, h } = labelMm()
  const lo = Math.min(w, h), hi = Math.max(w, h)
  const near = (a: number, b: number) => Math.abs(a - b) <= 2
  const orientation = h >= w ? 'portrait' as const : 'landscape' as const
  if (near(lo, 210) && near(hi, 297)) return { format: 'a4', orientation }
  if (near(lo, 215.9) && near(hi, 279.4)) return { format: 'letter', orientation }
  return { format: [Math.min(w, h) * 72 / 25.4, Math.max(w, h) * 72 / 25.4], orientation }
}

/** Render the current document server-side; resolves to the PDF blob. */
export async function renderDocumentPDF(): Promise<Blob> {
  if (!API) throw new Error('Server rendering is not enabled in this build (VITE_IMPORT_API unset).')
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (TOKEN) headers['x-olm-token'] = TOKEN
  const res = await fetch(`${API}/render/report`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      design: saveOlmObject(),
      data: { rows: state.batchData ?? [] },
      page: pageFormat(),
    }),
  })
  if (!res.ok) {
    const txt = await res.text().catch(() => '')
    throw new Error(`render service returned ${res.status}${txt ? ` — ${txt.slice(0, 200)}` : ''}`)
  }
  return res.blob()
}
